import type { FC } from "react";
import { useMemo } from "react";
import { swSettings } from "app/common/services/swSettings";
import { getColumnsPickerLiteProps, getTableExcelUrl } from "./CategoryLeadersTableTopUtils";
import {
    ButtonsContainer,
    DownloadExcelContainer,
    SearchContainer,
    TableInputContainer,
    TableTopContainer,
} from "./CategoryLeadersTableTop.styles";
import { SearchInput } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import type { ICategoryLeadersTableTopProps } from "./CategoryLeadersTableTop.types";
import { AddToDashboardWrapper } from "./Components/AddToDashboard";
import { useTableSearch } from "./Hooks/UseTableSearch";
import { useDidMountEffect } from "app/custom-hooks/useDidMountEffect";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";

export const CategoryLeadersTableTop: FC<ICategoryLeadersTableTopProps> = (props) => {
    const {
        onFilterChange,
        tableColumns,
        onClickToggleColumns,
        services,
        tableQueryParams,
        filtersStateObject,
        navParams,
        sort,
        excelApiEndpoint,
        metricType,
        totalCount,
    } = props;

    const { searchFilter, onSearch } = useTableSearch();

    const selectedCategory = useMemo(() => {
        return services.categoryService.categoryQueryParamToCategoryObject(navParams.category);
    }, [navParams.category, services.categoryService]);

    const columnPickerProps = useMemo(() => {
        return getColumnsPickerLiteProps(tableColumns, onClickToggleColumns);
    }, [tableColumns, onClickToggleColumns]);

    const tableExcelUrl = getTableExcelUrl({
        apiEndpoint: excelApiEndpoint,
        tableQueryParams: tableQueryParams,
        tableFilter: searchFilter,
        category: selectedCategory,
        services: services,
        tableSort: sort,
    });

    const isExcelEnabled = swSettings.current.resources.IsExcelAllowed;
    const isPaidExcelPermitted = isPaidExcelAllowed();

    /**
     * In case the table filters have changed, we want to update table with the new filters
     * (so that it'll execute a new API call)
     */
    useDidMountEffect(() => {
        onFilterChange(
            {
                filter: `${searchFilter}`,
            },
            false,
        );
    }, [onFilterChange, searchFilter]);

    return (
        <TableTopContainer>
            <TableInputContainer>
                <SearchContainer>
                    <SearchInput
                        defaultValue={""}
                        debounce={250}
                        onChange={onSearch}
                        placeholder={services.translate("table.search.placeholder.default")}
                    />
                </SearchContainer>
                <ButtonsContainer>
                    <DownloadExcelContainer>
                        <DownloadButtonMenu
                            Excel={true}
                            PaidExcel={isPaidExcelPermitted}
                            totalCount={totalCount}
                            downloadUrl={tableExcelUrl}
                            excelLocked={!isExcelEnabled}
                        />
                    </DownloadExcelContainer>
                    <div>
                        <ColumnsPickerLite
                            {...columnPickerProps}
                            withTooltip={true}
                            translations={{
                                tooltip: services.translate("workspace.sales.column.picker"),
                                getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                    services.translate("common.columnsPicker.selectedAmount", {
                                        selected: selectedValue,
                                        total: totalValue,
                                    }),
                                mandatoryField: services.translate("common.columnsPicker.mandatoryField"),
                                apply: services.translate("common.apply"),
                            }}
                        />
                    </div>
                    <div>
                        <AddToDashboardWrapper
                            filtersStateObject={filtersStateObject}
                            navParams={navParams}
                            tableFilters={searchFilter}
                            metricType={metricType}
                        />
                    </div>
                </ButtonsContainer>
            </TableInputContainer>
        </TableTopContainer>
    );
};
